import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight,
  faBoxOpen,
  faClock,
  faEnvelope,
  faLocationDot,
  faMagnifyingGlass,
  faPhone,
  faStore,
} from '@fortawesome/free-solid-svg-icons';

import Loader from '../Loader/Loader';
import './TiendaContent.css';

const ordenes = [
  { id: 'novedad', label: 'Novedad' },
  { id: 'precio-asc', label: 'Menor precio' },
  { id: 'precio-desc', label: 'Mayor precio' },
];

const TiendaInfo = ({ id: idProp }) => {
  const params = useParams();
  const id = idProp || params.id;
  const [tienda, setTienda] = useState(null);
  const [productos, setProductos] = useState([]);
  const [busqueda, setBusqueda] = useState('');
  const [categoria, setCategoria] = useState('todas');
  const [orden, setOrden] = useState('novedad');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const formatoPrecio = (valor) => new Intl.NumberFormat('es-AR', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
    style: 'currency',
    currency: 'ARS',
  }).format(Number(valor || 0));

  useEffect(() => {
    const obtenerTienda = async () => {
      try {
        setLoading(true);
        setError(null);
        const [response, productosResponse] = await Promise.all([
          fetch(`/api/tienda/${id}`),
          fetch(`/api/tienda/${id}/avisos`),
        ]);
        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(data.error || 'Error al obtener los datos de la tienda.');
        }

        setTienda(data);
        if (productosResponse.ok) {
          const dataProductos = await productosResponse.json();
          setProductos(Array.isArray(dataProductos) ? dataProductos : []);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    obtenerTienda();
  }, [id]);

  const categorias = useMemo(() => {
    const nombres = productos
      .map((producto) => producto.Categoria)
      .filter(Boolean);
    return ['todas', ...Array.from(new Set(nombres))];
  }, [productos]);

  const productosFiltrados = useMemo(() => {
    const termino = busqueda.trim().toLowerCase();
    const filtrados = productos.filter((producto) => {
      const coincideTexto = !termino || [
        producto.Titulo,
        producto.Descripcion,
        producto.Categoria,
      ].filter(Boolean).some((valor) => String(valor).toLowerCase().includes(termino));
      const coincideCategoria = categoria === 'todas' || producto.Categoria === categoria;
      return coincideTexto && coincideCategoria;
    });

    return [...filtrados].sort((a, b) => {
      if (orden === 'precio-asc') return Number(a.Precio || 0) - Number(b.Precio || 0);
      if (orden === 'precio-desc') return Number(b.Precio || 0) - Number(a.Precio || 0);
      return Number(b.Idaviso || 0) - Number(a.Idaviso || 0);
    });
  }, [busqueda, categoria, orden, productos]);

  if (loading) {
    return (
      <main className="tienda-view">
        <section className="tienda-state">
          <Loader />
          <p>Cargando tienda</p>
        </section>
      </main>
    );
  }

  if (error) {
    return (
      <main className="tienda-view">
        <section className="tienda-state">
          <h2>No pudimos cargar la tienda</h2>
          <p>{error}</p>
        </section>
      </main>
    );
  }

  if (!tienda) {
    return (
      <main className="tienda-view">
        <section className="tienda-state">
          <h2>Tienda no encontrada</h2>
        </section>
      </main>
    );
  }

  const ubicacion = [tienda.Direccion, tienda.Ciudad, tienda.Provincia, tienda.Pais]
    .filter(Boolean)
    .join(', ');

  return (
    <main className="tienda-view">
      <section className="tienda-hero">
        <div className="tienda-hero-copy">
          <p>Tienda Cannubis</p>
          <h1>{tienda.Nombre}</h1>
          <span className="tienda-status">
            {tienda.habilitada === false ? 'Deshabilitada' : 'Activa'}
            {tienda.tiendadestacada ? ' - Destacada' : ''}
          </span>
        </div>

        <div className="tienda-hero-media">
          {tienda.Imagen ? (
            <img src={tienda.Imagen} alt={tienda.Nombre} />
          ) : (
            <FontAwesomeIcon icon={faStore} />
          )}
        </div>
      </section>

      <section className="tienda-content-grid">
        <article className="tienda-description">
          <h2>Sobre la tienda</h2>
          <p>{tienda.Descripcion || 'Esta tienda todavia no cargo una descripcion.'}</p>
        </article>

        <aside className="tienda-contact">
          <h2>Datos comerciales</h2>
          <ul>
            <li>
              <FontAwesomeIcon icon={faLocationDot} />
              <span>{ubicacion || 'Ubicacion no informada'}</span>
            </li>
            <li>
              <FontAwesomeIcon icon={faPhone} />
              <span>{tienda.Telefono || 'Telefono no informado'}</span>
            </li>
            <li>
              <FontAwesomeIcon icon={faEnvelope} />
              <span>{tienda.CorreoElectronico || 'Correo no informado'}</span>
            </li>
            <li>
              <FontAwesomeIcon icon={faClock} />
              <span>{tienda.HorarioAtencion || 'Horario no informado'}</span>
            </li>
          </ul>
        </aside>
      </section>

      <section className="tienda-productos">
        <header className="tienda-productos-heading">
          <div>
            <span>Catalogo de {tienda.Nombre}</span>
            <h2>Productos disponibles</h2>
          </div>
          <strong>{productosFiltrados.length} de {productos.length}</strong>
        </header>

        <div className="tienda-productos-toolbar">
          <div className="tienda-productos-search">
            <FontAwesomeIcon icon={faMagnifyingGlass} />
            <input
              type="search"
              value={busqueda}
              onChange={(event) => setBusqueda(event.target.value)}
              placeholder="Buscar dentro de la tienda"
            />
          </div>

          <select value={categoria} onChange={(event) => setCategoria(event.target.value)} aria-label="Categoria">
            {categorias.map((item) => (
              <option key={item} value={item}>{item === 'todas' ? 'Todas las categorias' : item}</option>
            ))}
          </select>

          <select value={orden} onChange={(event) => setOrden(event.target.value)} aria-label="Orden">
            {ordenes.map((item) => (
              <option key={item.id} value={item.id}>{item.label}</option>
            ))}
          </select>
        </div>

        {productos.length === 0 ? (
          <div className="tienda-productos-vacio">
            <FontAwesomeIcon icon={faBoxOpen} />
            <p>Esta tienda todavia no tiene productos publicados.</p>
          </div>
        ) : productosFiltrados.length === 0 ? (
          <div className="tienda-productos-vacio">
            <FontAwesomeIcon icon={faBoxOpen} />
            <p>No encontramos productos con esos filtros.</p>
          </div>
        ) : (
          <div className="tienda-productos-grid">
            {productosFiltrados.map((producto) => (
              <Link key={producto.Idaviso} className="tienda-producto" to={`/aviso/${producto.Idaviso}`}>
                <div className="tienda-producto-media">
                  {producto.Imagen1 ? (
                    <img src={producto.Imagen1} alt={producto.Titulo} loading="lazy" />
                  ) : (
                    <FontAwesomeIcon icon={faBoxOpen} />
                  )}
                </div>
                <div className="tienda-producto-copy">
                  <span className="tienda-producto-category">{producto.Categoria || 'Producto'}</span>
                  <h3>{producto.Titulo}</h3>
                  <p>{producto.Descripcion}</p>
                  <div>
                    <strong>{formatoPrecio(producto.Precio)}</strong>
                    <span>{producto.Stockproducto} disponibles <FontAwesomeIcon icon={faArrowRight} /></span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </main>
  );
};

export default TiendaInfo;
